import {useState} from 'react'
import {useLayout} from '../../../_metronic/layout/core'
import {createCostCenter} from './api'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import toast from 'react-hot-toast'
import Select from 'react-select'
import {removeIndicatorSeparator, solidSelectStyles} from '../shared/ui/selectStyles'

// ─── Validation Schema ────────────────────────────────────────────────────────

const costCenterSchema = Yup.object().shape({
  center_code: Yup.string()
    .required('Cost center code is required')
    .matches(/^[a-zA-Z0-9\-_' ]{1,25}$/, {
      message:
        'Code must be 1-25 characters and can only contain letters, numbers, hyphens and underscores',
    }),
  center_name: Yup.string()
    .required('Cost center name is required')
    .matches(/^[a-zA-Z0-9\-' ]{1,100}$/, {
      message:
        'Name must be 1-100 characters and can only contain letters, numbers, hyphens, apostrophes and spaces',
    }),
  department: Yup.string().required('Department is required'),
  department_head: Yup.string().required('Manager/Head is required'),
  total_budget: Yup.number().min(0, 'Budget must be 0 or greater'),
  status: Yup.string().oneOf(['active', 'inactive']).required('Status is required'),
  description: Yup.string(),
})

// ─── Constants ────────────────────────────────────────────────────────────────

const DEPARTMENT_OPTIONS = [
  {value: 'sales', label: 'Sales'},
  {value: 'marketing', label: 'Marketing'},
  {value: 'operations', label: 'Operations'},
  {value: 'hr', label: 'HR'},
  {value: 'finance', label: 'Finance'},
  {value: 'it', label: 'IT'},
  {value: 'customer_service', label: 'Customer Service'},
  {value: 'logistics', label: 'Logistics'},
  {value: 'procurement', label: 'Procurement'},
  {value: 'others', label: 'Others'},
]

// ─── Types ────────────────────────────────────────────────────────────────────

interface CostCenterFormValues {
  center_code: string
  center_name: string
  department: string
  department_head: string
  total_budget: number
  status: string
  description: string
}

type OptionType = {
  value: string
  label: string
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CreateCostCenter: React.FC<{
  setCreateCostCenter: React.Dispatch<React.SetStateAction<boolean>>
  setShouldRefreshCostCenters: React.Dispatch<React.SetStateAction<boolean>>
}> = ({setCreateCostCenter, setShouldRefreshCostCenters}) => {
  const {company} = useLayout()
  const [loading, setLoading] = useState(false)
  const [department, setDepartment] = useState<OptionType>({
    label: 'Select Department...',
    value: '',
  })

  const initialValues: CostCenterFormValues = {
    center_code: '',
    center_name: '',
    department: '',
    department_head: '',
    total_budget: 0,
    status: 'active',
    description: '',
  }

  const formik = useFormik<CostCenterFormValues>({
    initialValues,
    validationSchema: costCenterSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setLoading(true)
      const body = {
        cost_center_id: null,
        company_id: company?.value,
        center_code: values.center_code,
        center_name: values.center_name,
        department: values.department,
        department_head: values.department_head,
        total_budget: values.total_budget || 0,
        status: values.status,
        description: values.description || '',
      }
      await createCostCenter(body)
        .then((res) => {
          res?.detail && toast.success('Cost Center has been added successfully.')
          formik.resetForm()
          setShouldRefreshCostCenters(true)
        })
        .catch((err) => toast.error(err?.detail || err.message))
        .finally(() => {
          setLoading(false)
          setCreateCostCenter(false)
        })
    },
  })

  // Sync department select back into formik
  const handleDepartmentChange = (option: any) => {
    setDepartment(option as OptionType)
    formik.setFieldValue('department', option.value, true)
  }

  return (
    <div className='card'>
      <div
        className='card-header border-0'
        data-bs-target='#kt_cost_center_form'
        aria-expanded='true'
        aria-controls='kt_cost_center_form'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Add New Cost Center</h3>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit} noValidate className='form'>
        <div
          id='kt_cost_center_form'
          className='collapse show d-flex justify-content-center flex-column flex-md-row'
        >
          {/* Left Column */}
          <div className='p-8 col pb-0 pb-md-4'>
            {/* Center Code */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Center Code</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Cost Center Code'
                  {...formik.getFieldProps('center_code')}
                />
                {formik.touched.center_code && formik.errors.center_code && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.center_code}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Center Name */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Center Name</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Cost Center Name'
                  {...formik.getFieldProps('center_name')}
                />
                {formik.touched.center_name && formik.errors.center_name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.center_name}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Department */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Department</label>
              <div className='col-lg-9 fv-row'>
                <Select
                  value={department}
                  onChange={handleDepartmentChange}
                  options={DEPARTMENT_OPTIONS}
                  isSearchable={true}
                  className='fw-bold'
                  styles={solidSelectStyles}
                  components={removeIndicatorSeparator}
                />
              </div>
            </div>

            {/* Department Head */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                Manager / Head
              </label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Manager / Head'
                  {...formik.getFieldProps('department_head')}
                />
                {formik.touched.department_head && formik.errors.department_head && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.department_head}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className='p-8 col pt-0 pt-md-8 pb-md-4'>
            {/* Total Budget */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6'>Total Budget (&#8377;)</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Total Budget'
                  {...formik.getFieldProps('total_budget')}
                />
                {formik.touched.total_budget && formik.errors.total_budget && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.total_budget}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Status */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Status</label>
              <div className='col-lg-9 fv-row d-flex'>
                <span className='form-check form-check-custom form-check-solid fs-6'>
                  Active{' '}
                  <input
                    className='form-check-input'
                    type='radio'
                    name='status'
                    value='active'
                    checked={formik.values.status === 'active'}
                    onChange={() => formik.setFieldValue('status', 'active')}
                    style={{marginLeft: '10px'}}
                  />
                </span>
                <span
                  style={{marginLeft: '20px'}}
                  className='form-check form-check-custom form-check-solid fs-6'
                >
                  Inactive{' '}
                  <input
                    className='form-check-input'
                    type='radio'
                    name='status'
                    value='inactive'
                    checked={formik.values.status === 'inactive'}
                    onChange={() => formik.setFieldValue('status', 'inactive')}
                    style={{marginLeft: '10px'}}
                  />
                </span>
              </div>
            </div>

            {/* Description */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6'>Description</label>
              <div className='col-lg-9 fv-row'>
                <textarea
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Description'
                  rows={4}
                  {...formik.getFieldProps('description')}
                />
                {formik.touched.description && formik.errors.description && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.description}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end px-9 border-0 pb-7 pt-0'>
          <button
            type='button'
            className='btn btn-secondary'
            style={{marginRight: '10px'}}
            onClick={() => {
              formik.resetForm()
              setCreateCostCenter(false)
              setShouldRefreshCostCenters(false)
            }}
          >
            Cancel
          </button>
          <button
            type='submit'
            className='btn btn-primary ml-2'
            disabled={loading || !formik.isValid}
          >
            {!loading ? 'Add Cost Center' : null}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
